import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Checkbox, Dropdown, Form, FormInstance, Modal as AModal } from 'antd';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import translations from 'translations';
import messages from './messages';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import CheckIcon from './Images/CheckIcon.svg';
import DropDownIndicator from './Images/DropDownIndicator.svg';
import { getInitials } from 'utils/getInitialsBrandName';
import CheckedButtonIcon from './Images/CheckedButton.svg';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/mousewheel';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';

const Container = styled.div`
  max-width: 1168px;
  margin-top: 16px;
  ${media.lg`
    margin: 24px 0px 8px;
  `};
`;

const DropdownComponent = styled(Dropdown)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  gap: 10px;
  ${({ fullWidth }) =>
    fullWidth &&
    `
    width: 100%;
    justify-content: space-between;
    height: 40px;
  `}
`;

const DropdownButton = styled(Button)`
  &:hover {
    color: rgba(0, 0, 0, 0.88) !important;
  }
  &:disabled:hover {
    color: rgba(0, 0, 0, 0.25) !important;
  }
`;

const DropDownOptions = styled.div`
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #eaebea;
  overflow: hidden;
  min-width: 200px;
  max-height: 320px;
  overflow: auto;
`;

const FormItem = styled(Form.Item)`
  .ant-form-item-label {
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
    color: #2f382c;
  }
`;

const FormItemDesktop = styled(Form.Item)`
  margin-bottom: 0px;
`;

const CheckboxItem = styled(Checkbox)`
  position: relative;
  padding: 11px 12px;
  color: #596056;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  &:hover {
    background: #f0f9f1;
    color: #43a047;
  }
  ${({ allOption }) =>
    allOption &&
    `
    position: absolute;
    background: #ffffff;
    z-index: 10;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    width: 99%;
    &:after {
        content: '';
        width: calc(100% - 24px);
        height: 1px;
        background: #EAEBEA;
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
    }
    `}
  ${({ firstCheck }) =>
    firstCheck &&
    `
        margin-top: 40px;
    `}
`;

const SingleOption = styled.div`
  padding: 11px 12px;
  color: #596056;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 400;
  &:hover,
  &.active {
    background: #f0f9f1;
    color: #43a047;
  }
`;

const DropDownIndicatorIcon = styled(SVGIcon)`
  ${({ rotate }) =>
    rotate &&
    `
    transform: rotate(-180deg);
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-item: center;
  gap: 12px;
  @media (max-width: 1023px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(90px, 1fr));
  }
  max-height: 165px;
  overflow-y: auto;
`;

const ButtonGroupDesktopContainer = styled.div`
  overflow: auto;
  flex-grow: 1;
  ${ButtonWrapper} {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE, Edge Legacy */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari */
    }
  }
  margin-left: 16px;
`;

const ButtonGroupMobileContainer = styled.div``;

const FlexContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ClearButtonWrapper = styled.div`
  flex-shrink: 0;
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
`;

const ClearButton = styled(Button)`
  cursor: pointer;
  padding: 0px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: #136fd8;
  font-weight: 500;
  &:hover {
    color: #136fd8 !important;
    opacity: 0.7;
    background: transparent !important;
  }
`;

const ButtonElement = styled(Button)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 16px;
  color: #596056;
  line-height: 18px;
  height: 40px;
  &.active {
    border-color: #43a047;
    color: #43a047;
    font-weight: 500;
  }
  ${({ roundBorder }) =>
    roundBorder &&
    `
    padding: 8px 12px;
    height: 32px;
    border-radius: 20px;
`}
  @media(max-width: 767px) {
    font-size: 13px;
    padding: 8px 12px;
  }
  span {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CheckedButtonIconEl = styled(SVGIcon)`
  position: absolute;
  top: 0px;
  right: 0px;
`;

const BrandName = styled.div`
  width: 20px;
  height: 20px;
  border: 1px solid #d9d9d9;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
`;

const BrandText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
  font-weight: 400;
`;

const SubCategoriesText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 400;
`;

const Modal = styled(AModal)`
  overflow: hidden;
  .ant-modal-header {
    position: relative;
    text-align: center;
    padding-bottom: 14px;
    margin-bottom: 24px;
    font-size: 16px;
    font-weight: 500;
    color: #2f382c;
    &:before {
      content: '';
      position: absolute;
      bottom: 0px;
      left: -12px;
      width: 107%;
      height: 1px;
      background: rgb(226, 226, 226);
    }
  }
  .ant-modal-content {
    padding: 12px;
  }
  .ant-modal-footer {
    display: flex;
    gap: 4px;
    button {
      flex-basis: 50% !important;
      height: 44px;
    }
  }
`;

const SpaceLabel = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: -2px;
`;

interface Option {
  label: string;
  value: string | number;
  image?: string;
}

interface IAggregationData {
  brands: Option[];
  origins: Option[];
  subCategories: Option[];
}

interface SearchFilterProps {
  form: FormInstance;
  aggregationData: IAggregationData;
  onSearch: (values: any) => void;
  sortOption: Option[];
  openModal?: boolean;
  onCloseModal?: () => void;
  canClearFilter: boolean;
  setCanClearFilter: (state: boolean) => void;
  persistFilter?: any;
}

interface FilterComponentProps {
  value?: (string | number)[];
  onChange?: (val: (string | number)[]) => void;
  label?: string;
  options: Option[];
  mode?: 'single' | 'multiple';
  roundBorder?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  placeHolder?: string;
  canDeselect?: boolean;
  showImage?: boolean;
}

const SelectCategoriesDropdown: React.FC<FilterComponentProps> = ({
  value = [],
  onChange,
  options,
  fullWidth,
  disabled,
  placeHolder,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef: any = useRef(null);
  const handleMultiChange = (list: CheckboxValueType[]) => {
    if (options?.length === 1) return;
    onChange?.(list as (string | number)[]);
  };

  const dropdownContent = useMemo(() => {
    return (
      <DropDownOptions>
        <Checkbox.Group
          value={value}
          onChange={handleMultiChange}
          style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
        >
          {options.map((option) => (
            <CheckboxItem key={option.value} value={option.value}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <SubCategoriesText>{option.label}</SubCategoriesText>
              </div>
            </CheckboxItem>
          ))}
        </Checkbox.Group>
      </DropDownOptions>
    );
  }, [options, value, onChange]);

  const dropDownLabel = useMemo(() => {
    if (!value?.length) {
      return placeHolder || '';
    }
    if (value?.length === 1 && value?.[0] === 0) {
      return options.map((option) => option.label)?.join(', ');
    }
    const label = value
      ?.map((vl) => {
        const option = options.find((option) => String(option.value) === String(vl));
        return option?.label;
      })
      ?.join(', ');
    return label;
  }, [fullWidth, options, value, placeHolder]);

  useEffect(() => {
    if (value?.length === 1 && value[0] === 0) {
      onChange?.(options.map((option) => Number(option.value)));
    }
  }, [value]);

  return (
    <DropdownComponent
      ref={dropdownRef}
      dropdownRender={() => dropdownContent}
      open={dropdownOpen}
      onOpenChange={(value) => {
        setDropdownOpen(value);
        if (!value) {
          dropdownRef?.current?.blur();
        }
      }}
      disabled={!options.length || disabled}
      fullWidth={fullWidth}
    >
      <DropdownButton>
        <SpaceLabel>{dropDownLabel}</SpaceLabel>
        <DropDownIndicatorIcon rotate={dropdownOpen} src={DropDownIndicator} />
      </DropdownButton>
    </DropdownComponent>
  );
};

const SelectDropdown: React.FC<FilterComponentProps> = ({
  value = [],
  onChange,
  label,
  options,
  mode = 'single',
  fullWidth,
  disabled,
  placeHolder,
  showImage,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef: any = useRef(null);

  const checkAllControl = useMemo(() => {
    if (mode === 'single') return {};
    const numberOfChecked = value?.reduce((acc: number, vl) => {
      const valueIndex = options.findIndex((option) => String(option.value) === String(vl));
      if (valueIndex !== -1) {
        acc += 1;
      }
      return acc;
    }, 0 as number);
    const allChecked = !!options.length && numberOfChecked === options.length;
    const indeterminate = !!numberOfChecked && !allChecked;
    return {
      allChecked: allChecked,
      indeterminate: indeterminate,
    };
  }, [options, value]);

  const handleMultiChange = (list: CheckboxValueType[]) => {
    onChange?.(list as (string | number)[]);
  };

  const handleSingleClick = (val: string | number) => {
    if (val === value?.[0]) return;
    onChange?.([val]);
  };
  const dropdownContent = useMemo(() => {
    if (mode === 'multiple') {
      return (
        <DropDownOptions>
          <CheckboxItem
            indeterminate={checkAllControl?.indeterminate}
            checked={checkAllControl?.allChecked}
            onChange={(e) => onChange?.(e.target.checked ? options.map((option) => option.value) : [])}
            allOption
          >
            {translations(messages.all)}
          </CheckboxItem>
          <Checkbox.Group
            value={value}
            onChange={handleMultiChange}
            style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
          >
            {options.map((option, index) => (
              <CheckboxItem key={option.value} value={option.value} firstCheck={index === 0}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {showImage && (
                    <>
                      {option.image ? (
                        <img src={option.image} alt={option.label} style={{ width: 20, height: 20 }} />
                      ) : (
                        <BrandName>{getInitials(option.label)}</BrandName>
                      )}
                    </>
                  )}
                  <BrandText>{option.label}</BrandText>
                </div>
              </CheckboxItem>
            ))}
          </Checkbox.Group>
        </DropDownOptions>
      );
    }

    return (
      <DropDownOptions>
        {options.map((option) => (
          <SingleOption
            key={option.value}
            onClick={() => handleSingleClick(option.value)}
            className={option.value === value?.[0] ? 'active' : ''}
          >
            {option.label}
            {option.value === value?.[0] && <SVGIcon src={CheckIcon} />}
          </SingleOption>
        ))}
      </DropDownOptions>
    );
  }, [mode, options, value, checkAllControl, onChange]);

  const dropDownLabel = useMemo(() => {
    if (fullWidth) {
      if (!value?.length) {
        return placeHolder || '';
      }
      const label = value
        ?.map((vl) => {
          const option = options.find((option) => String(option.value) === String(vl));
          return option?.label;
        })
        ?.join(', ');
      return label;
    }
    return label;
  }, [fullWidth, options, value, placeHolder]);

  return (
    <DropdownComponent
      ref={dropdownRef}
      dropdownRender={() => dropdownContent}
      open={dropdownOpen}
      onOpenChange={(value) => {
        setDropdownOpen(value);
        if (!value) {
          dropdownRef?.current?.blur();
        }
      }}
      //   getPopupContainer={(triggerNode) => triggerNode.parentNode}
      disabled={!options.length || disabled}
      fullWidth={fullWidth}
    >
      <DropdownButton>
        <SpaceLabel>{dropDownLabel}</SpaceLabel>
        <DropDownIndicatorIcon rotate={dropdownOpen} src={DropDownIndicator} />
      </DropdownButton>
    </DropdownComponent>
  );
};

const ButtonCategoriesGroup: React.FC<FilterComponentProps> = ({ options, value, onChange, disabled }) => {
  const swiperRef = useRef<any>(null);
  const currentValue = value || [];
  const [initScroll, setInitScroll] = useState(false);

  const handleClickButton = (val: string | number) => {
    if (options?.length === 1) return;
    if (val === 0) {
      onChange?.([val]);
      return;
    }
    if (currentValue.includes(val)) {
      let newValues = currentValue.filter((vl) => vl !== val);
      if (!newValues?.length && options?.[0]?.value === 0) {
        newValues = [0];
      }
      onChange?.(newValues);
    } else {
      let selectedValue = [...currentValue, val];
      const filteredSelectedValue = selectedValue?.filter((op) => !(op === 0));
      if (
        filteredSelectedValue?.length !== options?.filter((op) => !(op.value === 0))?.length &&
        selectedValue.includes(0)
      ) {
        selectedValue = filteredSelectedValue;
        onChange?.(selectedValue);
        return;
      }
      if (val === 0 || filteredSelectedValue.length === options?.filter((op) => !(op.value === 0))?.length) {
        selectedValue = [0];
        onChange?.(selectedValue);
        if (swiperRef.current) {
          swiperRef.current.slideTo(0, 300);
        }
        return;
      }
      onChange?.(selectedValue);
    }
  };

  useEffect(() => {
    if (currentValue?.length === options?.filter((op) => !(op.value === 0))?.length) {
      onChange?.([0]);
    }
  }, []);

  useEffect(() => {
    if (currentValue?.length && !initScroll) {
      const firstValue = currentValue[0];
      const firstValueIndex = options.findIndex((item) => item.value === firstValue);
      if (swiperRef.current && firstValueIndex !== -1) {
        swiperRef.current.slideTo(firstValueIndex, 300);
      }
      setInitScroll(true);
    }
    if (currentValue?.length === 1 && currentValue[0] === 0) {
      if (swiperRef.current) {
        swiperRef.current.slideTo(0, 300);
      }
    }
  }, [currentValue]);

  return (
    <Swiper
      onSwiper={(swiper) => {
        swiperRef.current = swiper;
      }}
      slidesPerView="auto"
      spaceBetween={12}
      className="button-categories-swiper"
      style={{ width: '100%', marginRight: '16px' }}
      modules={[Mousewheel]}
      mousewheel={{
        forceToAxis: true,
        sensitivity: 3,
        releaseOnEdges: true,
        thresholdDelta: 10,
        thresholdTime: 50,
      }}
    >
      {options.map((option) => (
        <SwiperSlide key={option.value} style={{ width: 'auto' }}>
          <ButtonElement
            onClick={() => handleClickButton(option.value)}
            className={currentValue.includes(option.value) || options.length === 1 ? 'active' : ''}
            roundBorder={true}
            disabled={disabled}
          >
            {option.label}
          </ButtonElement>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const ButtonGroup: React.FC<FilterComponentProps> = ({
  options,
  mode = 'single',
  roundBorder,
  value,
  onChange,
  disabled,
  canDeselect,
}) => {
  const currentValue = value || [];

  const handleClickButton = (val: string | number) => {
    if (mode === 'single') {
      if (!currentValue.includes(val)) {
        onChange?.([val]);
      } else {
        if (canDeselect) {
          onChange?.([options?.[0]?.value]);
        }
      }
      return;
    }
    if (currentValue.includes(val)) {
      const newValues = currentValue.filter((vl) => vl !== val);
      onChange?.(newValues);
    } else {
      onChange?.([...currentValue, val]);
    }
  };
  return (
    <ButtonWrapper>
      {options.map((option) => (
        <ButtonElement
          key={option.value}
          onClick={() => handleClickButton(option.value)}
          className={currentValue.includes(option.value) ? 'active' : ''}
          roundBorder={roundBorder}
          disabled={disabled}
        >
          {option.label}
          {!!currentValue.includes(option.value) && mode === 'multiple' && (
            <CheckedButtonIconEl src={CheckedButtonIcon} />
          )}
        </ButtonElement>
      ))}
    </ButtonWrapper>
  );
};

export const SearchFilter: React.FC<SearchFilterProps> = ({
  aggregationData,
  form,
  onSearch,
  sortOption,
  canClearFilter,
  setCanClearFilter,
}) => {
  const handleClearFilter = () => {
    if (canClearFilter) {
      form.setFieldsValue({
        sort: [],
        origins: [],
        brands: [],
      });
      if (aggregationData?.subCategories?.length > 1) {
        form.setFieldsValue({
          subCategory: [0],
        });
      }
      setCanClearFilter(false);
      form.submit();
    }
  };

  const isDisabledSort = useMemo(() => {
    return Object.values(aggregationData).every((aggregation) => !aggregation?.length);
  }, [aggregationData]);

  return (
    <Container>
      <Form
        onFinish={(values) => {
          onSearch(values);
        }}
        form={form}
        layout="vertical"
        onValuesChange={(_, values) => {
          const { subCategory, ...rest } = values;
          const formHasValue =
            Object.values(rest).some((value: any) => !!value?.length) || (subCategory?.length && subCategory[0] !== 0);
          if (formHasValue) {
            setCanClearFilter(true);
          } else {
            setCanClearFilter(false);
          }
          form.submit();
        }}
      >
        <FlexContainer>
          <FormItemDesktop name="sort">
            <SelectDropdown label={translations(messages.sortBy)} options={sortOption} disabled={isDisabledSort} />
          </FormItemDesktop>
          <FormItemDesktop name="origins">
            <SelectDropdown label={translations(messages.origins)} options={aggregationData.origins} mode="multiple" />
          </FormItemDesktop>
          <FormItemDesktop name="brands">
            <SelectDropdown
              label={translations(messages.brand)}
              options={aggregationData.brands}
              mode="multiple"
              showImage
            />
          </FormItemDesktop>
          {!!aggregationData.subCategories?.length && (
            <ButtonGroupDesktopContainer>
              <FormItemDesktop name="subCategory">
                <ButtonCategoriesGroup options={aggregationData.subCategories} />
              </FormItemDesktop>
            </ButtonGroupDesktopContainer>
          )}
          {canClearFilter && (
            <ClearButtonWrapper>
              <ClearButton type="text" onClick={handleClearFilter}>
                {translations(messages.clearButton)}
              </ClearButton>
            </ClearButtonWrapper>
          )}
        </FlexContainer>
      </Form>
    </Container>
  );
};

export const SearchFilterModal: React.FC<SearchFilterProps> = ({
  aggregationData,
  form,
  onSearch,
  sortOption,
  openModal,
  onCloseModal,
  canClearFilter,
  setCanClearFilter,
  persistFilter,
}) => {
  const [canFilter, setCanFilter] = useState(false);

  const isDisabledSort = useMemo(() => {
    return Object.values(aggregationData).every((aggregation) => !aggregation?.length);
  }, [aggregationData]);

  const subCategoriesOption = useMemo(() => {
    return aggregationData?.subCategories?.filter((option) => option.value !== 0) || [];
  }, [aggregationData?.subCategories]);

  const handleOk = () => {
    form.submit();
    onCloseModal?.();
  };

  const handleClearFilter = () => {
    form.setFieldsValue({
      sort: [],
      origins: [],
      brands: [],
      subCategory: [0],
    });
    if (aggregationData?.subCategories?.length > 1) {
      form.setFieldsValue({
        subCategory: [0],
      });
    }
    if (canClearFilter) {
      form.submit();
      setCanClearFilter(false);
    }
    setCanFilter(false);
  };

  const handleCancel = () => {
    onCloseModal?.();
    setCanFilter(false);
    form.setFieldsValue({
      brands: persistFilter?.brands || [],
      subCategory: persistFilter?.subCategory || [0],
      sort: persistFilter?.sort || [],
      origins: persistFilter?.origins || [],
    });
  };

  return (
    <Container>
      <Modal
        title={translations(messages.advancedFilter)}
        open={openModal}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        okText={translations(messages.filterButton)}
        okButtonProps={{ disabled: !canFilter }}
        cancelButtonProps={{ onClick: handleClearFilter }}
        cancelText={translations(messages.clearFilterButton)}
      >
        <Form
          onFinish={(values) => {
            onSearch(values);
            const { subCategory, ...rest } = values;
            const hasFilterValue = Object.values(rest).some((vl: any[]) => vl?.length);
            if (hasFilterValue) {
              setCanClearFilter(true);
            } else {
              if (subCategory?.length && subCategoriesOption?.length !== subCategory?.length) {
                setCanClearFilter(true);
              } else {
                setCanClearFilter(false);
              }
            }
          }}
          form={form}
          layout="vertical"
          onValuesChange={(_, values) => {
            const { subCategory, ...rest } = values;
            const formHasValue =
              Object.values(rest).some((value: any) => !!value?.length) ||
              (subCategory?.length && (subCategory?.length !== subCategoriesOption?.length || subCategory?.length > 1));
            if (formHasValue) {
              setCanFilter(true);
            } else {
              setCanFilter(false);
            }
          }}
        >
          <FormItem name="subCategory" label={translations(messages.categories)}>
            <SelectCategoriesDropdown
              fullWidth
              mode="multiple"
              label={translations(messages.categories)}
              options={aggregationData.subCategories.filter((option) => option.value !== 0)}
              placeHolder={translations(messages.categoriesPlaceHolder)}
            />
          </FormItem>
          <FormItem name="brands" label={translations(messages.brand)}>
            <SelectDropdown
              fullWidth
              label={translations(messages.brand)}
              options={aggregationData.brands}
              mode="multiple"
              placeHolder={translations(messages.brandsPlaceHolder)}
              showImage
            />
          </FormItem>
          <ButtonGroupMobileContainer>
            <FormItem name="sort" label={translations(messages.sortBy)}>
              <ButtonGroup options={sortOption} disabled={isDisabledSort} />
            </FormItem>
          </ButtonGroupMobileContainer>
          <FormItem name="origins" label={translations(messages.origins)}>
            <ButtonGroup label={translations(messages.origins)} options={aggregationData.origins} mode="multiple" />
          </FormItem>
        </Form>
      </Modal>
    </Container>
  );
};
